import { useContext } from 'react';
import { useDispatch } from 'react-redux';
import { Buttons, Notification, Text, Spinner, FlexContainer } from '@taxdown/piccolo-web';
import { locizeNS } from 'Services/i18n';
import { history } from 'Store';
import { setStageAction } from 'Store/modules/validations/actions';
import KamiContext from 'Context/KamiContext';
import useScreen from 'Hooks/useScreen';
import { useTranslation } from 'Hooks/useTranslation';
import { useGetUserPlan } from 'Containers/Validations/hooks';
import { FRONT_PLANS } from 'Constants/plans';
import { routes } from 'Constants/routes';
import { RecordType, ResponseType, ScreenPropsType } from 'Types/common';
import { useSaveUserData } from './hooks';
import * as Styled from './styles';

const EndScreen = ({ screenName }: ScreenPropsType): JSX.Element => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const plan = useGetUserPlan();
  const { kamiSama } = useContext(KamiContext);
  const { screen, renderForm, onNextScreen } = useScreen(screenName);
  const handleOnClick = (): void => history.push(routes.PLANS);
  const isBasicUser = plan === FRONT_PLANS.BASIC;

  const onSuccess = (response: ResponseType<RecordType>): void => {
    const responseData = response.data;
    const nextStage = responseData.item.stage;
    dispatch(setStageAction(nextStage));
  };

  const { isLoading, isError } = useSaveUserData(
    {
      stage: screen.getSection(),
      data: kamiSama?.getUserData(),
      next: true,
    },
    screen.getSection(),
    onSuccess
  );

  const { header, upsellingInfo, subheader, imageUrl } = renderForm;

  return (
    <Styled.ValidationContent type="start">
      <Styled.Container direction="column" align="center" gap="dimension_24">
        <FlexContainer
          direction="column"
          align="center"
          justify="center"
          fullHeight
          gap="dimension_20"
        >
          {imageUrl && <Styled.ImageContainer src={imageUrl} alt="start-img" />}
          <Text type="paragraphs" size="XL_L_S" color="dark" align="center">
            {header}
          </Text>
        </FlexContainer>
        {upsellingInfo && subheader && (
          <Notification
            mode="light"
            title={subheader}
            description={upsellingInfo}
            id={`${screenName}_upselling`}
            buttonLabel={t(`${locizeNS.PLANS}:UpsellingCard.CTA`)}
            buttonOnClick={isBasicUser ? handleOnClick : undefined}
            noClose
            mb="fds_size_40"
          />
        )}
        <Styled.ButtonsContainer fullWidth justify="center">
          <Buttons.Button
            mode="primary"
            size="m"
            label={
              isLoading ? (
                <Spinner size="M" justify="center" mode="secondary" align="center" />
              ) : (
                t('Button.continue')
              )
            }
            disabled={isLoading || isError}
            onClick={() => onNextScreen()}
          />
        </Styled.ButtonsContainer>
      </Styled.Container>
    </Styled.ValidationContent>
  );
};

export default EndScreen;
